import { useCallback, useMemo } from 'react';
import { Tree } from 'react-arborist';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { NodeApi } from 'react-arborist';

import { DependencyFilterKeys } from '../../../../../../shared/dependenciesV2';
import useVendoredDependencyPaths from '../../../../../hooks/api/vendoredDependencies/useVendoredDependencyPaths';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { formatPathForId } from '../../../snippets/utils';
import useFetchDependencyAction from '../../hooks/useFetchDependencyAction';

import buildTree from './buildTree';
import TreeViewLoader from './TreeViewLoader';
import VendoredDependenciesTreeViewNode from './VendoredDependenciesTreeViewNode';
import type { VendoredDependencyTreeNode } from './buildTree';

import styles from './VendoredPathTreeFilter.module.less';

/**
 * Fallback tree height used before the resizable layout has measured the tree
 * pane. react-arborist needs a numeric height to virtualize its rows.
 */
const FALLBACK_TREE_HEIGHT = 550;

type Props = {
  /**
   * The user project revision locator.
   */
  revisionLocator: string;
  /** Width of the tree pane, measured by the resizable file-tree layout. */
  width?: number;
  /** Height of the tree pane, measured by the resizable file-tree layout. */
  height?: number;
};

/**
 * Tree filter component for vendored dependencies.
 * Displays a file tree that updates the vendoredPath filter in Redux when a node is selected.
 */
const VendoredPathTreeFilter: VoidFunctionComponent<Props> = ({ revisionLocator, width, height }) => {
  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const setFilters = useFetchDependencyAction(DependenciesV2Store.actions.setFilters);

  const { data: paths, isLoading } = useVendoredDependencyPaths(revisionLocator);

  // Transform flat paths array into hierarchical tree structure
  const treeData = useMemo(() => {
    // Vendored deps always appear in a directory, so we always want to include a trailing slash.
    const shouldIncludeTrailingSlash = true;
    if (!paths?.paths) {
      return [];
    }
    return buildTree(paths.paths, shouldIncludeTrailingSlash);
  }, [paths]);

  const handleSelect = useCallback((nodes: Array<NodeApi<VendoredDependencyTreeNode>>) => {
    // react-arborist allows multi-select, which is why it passes us an array of nodes.
    // We only want to select the first node, so we take the first one.
    const node = nodes[0];
    if (node) {
      setFilters({
        ...filters,
        [DependencyFilterKeys.VendoredPath]: node.data.path,
      });
    }
  }, [filters, setFilters]);

  const selectedPath = filters.vendoredPath || undefined;

  if (isLoading) {
    return (
      <div className={styles.TreeContainer}>
        <TreeViewLoader />
      </div>
    );
  }

  return (
    <div className={styles.TreeContainer}>
      <Tree<VendoredDependencyTreeNode>
        data={treeData}
        openByDefault={false}
        // To determine the react-arborist internal ID for this node,
        // we need to ensure that the trailing slash on directories is correct.
        idAccessor={(node) => formatPathForId(node.path, node.type)}
        childrenAccessor="children"
        // Layout. Width and height come from the resizable file-tree layout.
        width={width}
        height={height ?? FALLBACK_TREE_HEIGHT}
        rowHeight={32}
        indent={8}
        // Selection logic
        selection={selectedPath}
        onSelect={handleSelect}
        disableMultiSelection
        disableDrag
      >
        {VendoredDependenciesTreeViewNode}
      </Tree>
    </div>
  );
};

export default VendoredPathTreeFilter;
